import {
  CartLocation,
  EMPTY_ADD_TO_CART_METADATA,
  STOREFRONT_CART_ATTRIBUTES,
} from "@tablescape/constants";
import { TablescapeContext, TablescapeEvent } from "@tablescape/context";
import { type TablescapeState } from "@tablescape/state";
import { formatMsrpValue } from "@tablescape/utilities";

const getBundleProduct = (
  node: HTMLElement,
  currentVariants: {
    bundleTitle: string | undefined;
    hasShippingNote: boolean;
    id: string;
    isProductBundle: boolean;
    isStandaloneBundleKit: boolean;
    isVariantBundle: boolean;
    msrp: string;
  }[],
  isShopifyBundle: boolean
) => {
  const {
    mtrBundleCompareAtPrice: bundleCompareAtPrice,
    mtrBundleId: bundleId,
    mtrBundleName: bundleTitle,
    mtrBundlePrice: bundlePrice,
    mtrProductImageSrc: bundleProductImageSrc,
  } = node.closest<HTMLElement>("[data-mtr-bundle-id]")?.dataset ?? {};

  if (
    !bundleId ||
    !bundleProductImageSrc ||
    !bundleTitle ||
    !bundlePrice ||
    !bundleCompareAtPrice
  ) {
    return {
      items: [],
      bundleProduct: undefined,
    };
  }

  const currentBundleDataset =
    node.querySelector<HTMLSelectElement>(`select[name="variant-options"]`)
      ?.selectedOptions?.[0]?.dataset ?? {};

  const currentBundleContainerId = currentBundleDataset.containerBundleId;
  const isStandaloneBundleKit = currentVariants.every(
    (variant) => variant.isStandaloneBundleKit
  );

  let items = [];

  if (isShopifyBundle && currentBundleContainerId) {
    items = [
      {
        attributes: [
          {
            key: STOREFRONT_CART_ATTRIBUTES.MSRP,
            value: String(formatMsrpValue(bundlePrice)),
          },
        ],
        id: currentBundleContainerId,
      },
    ];
  } else {
    items = currentVariants.map((variant) => ({
      attributes: [
        {
          key: STOREFRONT_CART_ATTRIBUTES.MSRP,
          value: String(formatMsrpValue(variant.msrp)),
        },
        ...(variant.hasShippingNote
          ? [
              {
                key: STOREFRONT_CART_ATTRIBUTES.PREORDER,
                value: String(true),
              },
            ]
          : []),
        ...(isStandaloneBundleKit
          ? [
              {
                key: STOREFRONT_CART_ATTRIBUTES.BUNDLES,
                value: JSON.stringify([bundleTitle]),
              },
              {
                key: STOREFRONT_CART_ATTRIBUTES.STANDALONE_BUNDLE_KIT,
                value: String(true),
              },
            ]
          : []),
      ],
      id: variant.id,
    }));
  }

  return {
    items,
    bundleProduct: {
      compareAtPrice: parseInt(bundleCompareAtPrice) * 100,
      id: bundleId,
      imageURL: bundleProductImageSrc,
      title: bundleTitle,
      price: parseInt(bundlePrice) * 100,
    },
  };
};

const getBundleVariants = (node: HTMLElement, isShopifyBundle: boolean) => {
  const currentBundleSelect = node.querySelector<HTMLSelectElement>(
    `select[name="variant-options"]`
  );
  const currentBundleDataset =
    currentBundleSelect?.selectedOptions?.[0]?.dataset ?? {};

  const {
    bundleCompareAtPrice,
    bundleId,
    bundlePrice,
    bundleProductImageSrc,
    bundleTitle,
  } = currentBundleDataset;

  if (
    !bundleId ||
    !bundleProductImageSrc ||
    !bundleTitle ||
    !bundlePrice ||
    !bundleCompareAtPrice
  ) {
    return {
      items: [],
      bundleProduct: undefined,
    };
  }

  let items = [];

  const currentBundleContainerId = currentBundleDataset.containerBundleId;

  const currentBundleVariantIds: string[] = JSON.parse(
    currentBundleDataset.variantBundleIds ?? "[]"
  );
  const currentBundleVariantCompareAtPrices: string[] = JSON.parse(
    currentBundleDataset.variantBundleCompareAtPrices ?? "[]"
  );
  const currentBundleVariantPrices: string[] = JSON.parse(
    currentBundleDataset.variantBundlePrices ?? "[]"
  );
  const currentBundleVariantShippingNotes: string[] = JSON.parse(
    currentBundleDataset.variantBundleShippingNotes?.replaceAll(
      "&quot;",
      '"'
    ) ?? "[]"
  );

  if (isShopifyBundle && currentBundleContainerId) {
    items = [
      {
        attributes: [
          {
            key: STOREFRONT_CART_ATTRIBUTES.MSRP,
            value: String(formatMsrpValue(bundlePrice)),
          },
        ],
        id: currentBundleContainerId,
      },
    ];
  } else {
    items = currentBundleVariantIds.map((variantId, index) => ({
      attributes: [
        {
          key: STOREFRONT_CART_ATTRIBUTES.MSRP,
          value: String(
            currentBundleVariantCompareAtPrices[index] ||
              currentBundleVariantPrices[index]
          ),
        },
        ...(currentBundleVariantShippingNotes[index]
          ? [
              {
                key: STOREFRONT_CART_ATTRIBUTES.PREORDER,
                value: String(true),
              },
            ]
          : []),
      ],
      id: variantId,
    }));
  }

  return {
    items,
    bundleProduct: {
      compareAtPrice: parseInt(bundleCompareAtPrice) * 100,
      id: bundleId,
      imageURL: bundleProductImageSrc,
      title: bundleTitle,
      price: parseInt(bundlePrice) * 100,
    },
  };
};

export const takeAddToCartAction = (
  lines: TablescapeState["cart"]["lines"],
  bundleProduct?: TablescapeState["cart"]["bundleProduct"]
) => {
  requestAnimationFrame(() => {
    TablescapeContext.emit(TablescapeEvent.TOGGLE_CART, {
      ui: {
        cartDrawerOpen: true,
        cartDrawerUserToggled: false,
        giftNoteDrawerOpen: false,
        productVariantDrawerOpen: false,
      },
    });

    requestAnimationFrame(() => {
      if (bundleProduct) {
        TablescapeContext.emit(TablescapeEvent.ADD_BUNDLE_TO_CART, {
          cart: {
            lines,
            bundleProduct,
            metadata: {
              ...EMPTY_ADD_TO_CART_METADATA,
              location: CartLocation.PDP_PRODUCT_HERO,
            },
          },
        });
        return;
      }
      TablescapeContext.emit(TablescapeEvent.ADD_TO_CART, {
        cart: {
          lines,
          metadata: {
            ...EMPTY_ADD_TO_CART_METADATA,
            location: CartLocation.PDP_PRODUCT_HERO,
          },
        },
      });
    });
  });
};

export const action = async (e: MouseEvent) => {
  const addToCart = e.target as HTMLElement;
  e.preventDefault();

  if (!addToCart) {
    return;
  }

  const formElement = document.querySelector<HTMLFormElement>(
    `form[id="product-form"]`
  );

  if (!formElement) {
    return;
  }

  const isShopifyBundle = formElement.dataset.shopifyBundle === "true";

  const currentVariants = Array.from(
    formElement.querySelectorAll<HTMLSelectElement>(
      `select[name="variant-options"]`
    )
  )
    .filter((select) => select.selectedOptions.length > 0)
    .map((select) => ({
      bundleTitle: select.selectedOptions[0].dataset.bundleTitle,
      id: select.selectedOptions[0].value,
      isProductBundle:
        select.selectedOptions[0].dataset.isProductBundle === "true",
      isVariantBundle:
        select.selectedOptions[0].dataset.isVariantBundle === "true",
      isStandaloneBundleKit:
        select.selectedOptions[0].dataset.isStandaloneBundleKit === "true",
      hasShippingNote:
        select.selectedOptions[0].dataset.hasShippingNote === "true",
      msrp: select.selectedOptions[0].dataset.productVariantMsrp ?? "",
    }));

  if (!currentVariants.length) {
    return;
  }

  const isProductBundle = currentVariants.every(
    (variant) => variant.isProductBundle
  );
  const isVariantBundle = currentVariants.every(
    (variant) => variant.isVariantBundle
  );

  const currentAutoAddVariantIds = [
    ...formElement.querySelectorAll<HTMLSelectElement>(
      `select[name="variant-options"]`
    ),
  ]
    .filter((select) => select.selectedOptions.length > 0)
    .reduce((flattenedVariantIds, selectElement) => {
      const autoAddVariantIds =
        selectElement.dataset.autoAddVariantIds?.split(",");
      if (autoAddVariantIds) {
        return [...flattenedVariantIds, ...autoAddVariantIds];
      } else {
        return flattenedVariantIds;
      }
    }, [] as string[])
    .filter((variantId) => variantId);

  const currentAutoAddVariantHasShippingNotes = formElement
    ?.querySelector<HTMLSelectElement>(`select[name="variant-options"]`)
    ?.dataset.autoAddVariantHasShippingNotes?.split(",");
  const bundleAutoAddVariantIds =
    formElement
      .querySelector<HTMLSelectElement>(
        `select[name="variant-options"][data-bundle-auto-add-variant-ids]`
      )
      ?.dataset.bundleAutoAddVariantIds?.split(",") ?? [];

  const allAutoAddVariantIds = currentAutoAddVariantIds
    .concat(bundleAutoAddVariantIds)
    .filter((variantId) => variantId);
  const autoAddVariantLines = allAutoAddVariantIds.map((id, index) => ({
    attributes: [
      ...(currentAutoAddVariantHasShippingNotes?.[index] === "true"
        ? [
            {
              key: STOREFRONT_CART_ATTRIBUTES.PREORDER,
              value: String(true),
            },
          ]
        : []),
    ],
    id,
    quantity: 1,
  }));

  try {
    if (isProductBundle || isVariantBundle) {
      const { bundleProduct, items } = isProductBundle
        ? getBundleProduct(formElement, currentVariants, isShopifyBundle)
        : getBundleVariants(formElement, isShopifyBundle);

      const lines = [
        ...items.map((item) => ({
          ...item,
          id: item.id,
          quantity: 1,
        })),
        ...autoAddVariantLines,
      ];

      takeAddToCartAction(lines, bundleProduct);
    } else {
      const currentBundleSelect = formElement.querySelector<HTMLSelectElement>(
        `select[name="variant-options"]`
      );
      const currentBundleDataset =
        currentBundleSelect?.selectedOptions?.[0]?.dataset ?? {};

      const { bundleTitle } = currentBundleDataset;

      const [
        {
          hasShippingNote,
          id: productCurrentVariantId,
          isStandaloneBundleKit,
          msrp: productSelectedVariantMsrp,
        },
      ] = currentVariants;

      const lines = [
        {
          attributes: [
            {
              key: STOREFRONT_CART_ATTRIBUTES.MSRP,
              value: String(formatMsrpValue(productSelectedVariantMsrp)),
            },
            ...(hasShippingNote
              ? [
                  {
                    key: STOREFRONT_CART_ATTRIBUTES.PREORDER,
                    value: String(true),
                  },
                ]
              : []),
            ...(isStandaloneBundleKit
              ? [
                  {
                    key: STOREFRONT_CART_ATTRIBUTES.BUNDLES,
                    value: JSON.stringify([bundleTitle]),
                  },
                  {
                    key: STOREFRONT_CART_ATTRIBUTES.STANDALONE_BUNDLE_KIT,
                    value: String(true),
                  },
                ]
              : []),
          ],
          id: productCurrentVariantId,
          quantity: 1,
        },
        ...autoAddVariantLines,
      ];
      takeAddToCartAction(lines);
    }
  } catch (error) {
    console.error(error);
  }
};
